import { Box, Typography, Tooltip, IconButton, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import DoneIcon from '@mui/icons-material/Done';
import BusinessIcon from '@mui/icons-material/Business';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import LinkIcon from '@mui/icons-material/Link';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PropTypes from 'prop-types';

const ObjectStatItem = ({
	icon: Icon,
	label,
	value,
	subtitle,
	iconColor,
	showBorder,
	backgroundColor,
}) => {
	const theme = useTheme();

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 1,
				flex: 1,
				paddingTop: '18px',
				paddingBottom: '18px',
				paddingRight: showBorder ? 2.5 : 0,
				borderRight: showBorder ? `1px solid ${theme.palette.divider}` : 'none',
			}}
		>
			<Icon
				data-testid={`${label.toLowerCase().replace(/\s+/g, '-')}-icon`}
				sx={{
					fontSize: '24px',
					color: iconColor || '#7c7e8c',
					mb: 0.5,
					backgroundColor: backgroundColor || 'transparent',
					padding: '6.5px',
					borderRadius: '7px',
					width: '28px',
					height: '28px',
				}}
			/>

			<Typography
				variant='caption'
				sx={{
					fontSize: '11px',
					fontWeight: 550,
					color: theme.palette.text.secondary,
					textTransform: 'uppercase',
					letterSpacing: '0.7px',
				}}
			>
				{label}
			</Typography>

			<Typography
				variant='h4'
				sx={{
					fontSize: '24px',
					fontWeight: 600,
					color: theme.palette.text.primary,
					lineHeight: 1,
					mb: 0.5,
				}}
			>
				{value?.toLocaleString() ?? 0}
			</Typography>

			<Typography
				variant='body2'
				sx={{
					fontSize: '11px',
					color: theme.palette.text.secondary,
					lineHeight: '16px',
					letterSpacing: '0.25px',
				}}
			>
				{subtitle}
			</Typography>
		</Box>
	);
};

ObjectStatItem.propTypes = {
	icon: PropTypes.elementType.isRequired,
	label: PropTypes.string.isRequired,
	value: PropTypes.number,
	subtitle: PropTypes.string.isRequired,
	iconColor: PropTypes.string,
	showBorder: PropTypes.bool,
	backgroundColor: PropTypes.string,
};

const AdminEmailObjectSyncedStatistics = ({ jobStatistics }) => {
	const theme = useTheme();
	const syncedJobsCount = jobStatistics.synced_jobs_count ?? 0;

	const Header = () => (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				padding: '14px 20px',
				borderBottom: `1px solid ${theme.palette.divider}`,
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					flexWrap: 'wrap',
				}}
			>
				<Typography
					variant='body1'
					sx={{
						fontSize: '13px',
						fontWeight: 600,
						color: theme.palette.text.primary,
					}}
				>
					CRM Records Created
				</Typography>

				<Chip
					icon={
						<DoneIcon
							data-testid='done-icon'
							sx={{ fontSize: '10px !important' }}
						/>
					}
					label='Successfully synced · No errors · No existing matches'
					size='small'
					sx={{
						backgroundColor: '#e8f5e9',
						color: '#2e7d32',
						fontSize: '11px',
						fontWeight: '500',
						letterSpacing: '0.25px',
						border: '1px solid #b6e6c8',
						height: '24px',
						borderRadius: '16px',
						padding: '2px 10px',
						paddingLeft: '5px',
						'& .MuiChip-icon': {
							color: '#2e7d32',
						},
						'& .MuiChip-label': {
							padding: 0,
							paddingLeft: '8px',
						},
					}}
				/>
				<Tooltip
					title='Totals only include records newly created from fully Synced jobs. Records that encountered errors during sync or were matched to existing CRM entries are excluded.'
					arrow
					placement='top'
				>
					<IconButton
						size='small'
						sx={{
							backgroundColor: '#f5f5f5',
							color: '#666666',
							width: '14px',
							height: '14px',
							'&:hover': {
								backgroundColor: '#e0e0e0',
							},
						}}
					>
						<QuestionMarkIcon
							data-testid='question-mark-icon'
							sx={{
								fontSize: '9px',
							}}
						/>
					</IconButton>
				</Tooltip>
			</Box>

			<Typography
				variant='body2'
				sx={{
					fontSize: '12px',
					color: theme.palette.text.secondary,
				}}
			>
				Across{' '}
				<Typography
					component='span'
					sx={{
						fontSize: '12px',
						color: theme.palette.text.secondary,
						fontWeight: 600,
					}}
				>
					{syncedJobsCount}
				</Typography>{' '}
				synced jobs
			</Typography>
		</Box>
	);

	return (
		<Box
			sx={{
				backgroundColor: theme.palette.background.paper,
				borderRadius: '8px',
				boxShadow: '0 1px 5px rgba(0, 0, 0, 0.1)',
				border: `1px solid ${theme.palette.divider}`,
				mb: 2,
			}}
		>
			<Header />

			<Box
				sx={{
					display: 'flex',
					gap: '20px',
					paddingLeft: '20px',
					paddingRight: '20px',
				}}
			>
				<ObjectStatItem
					icon={BusinessIcon}
					label='Accounts'
					value={jobStatistics.total_accounts_synced}
					subtitle='New companies added'
					iconColor='#3b5bdb'
					showBorder={true}
					backgroundColor='#eaf0ff'
				/>
				<ObjectStatItem
					icon={PersonOutlineOutlinedIcon}
					label='Contacts'
					value={jobStatistics.total_contacts_synced}
					subtitle='New people added'
					iconColor='#1D8348'
					showBorder={true}
					backgroundColor='#f0faf4'
				/>
				<ObjectStatItem
					icon={ChatBubbleOutlineOutlinedIcon}
					label='Interactions'
					value={jobStatistics.total_interactions_synced}
					subtitle='Email threads logged'
					iconColor='#862E9C'
					showBorder={true}
					backgroundColor='#fdf4ff'
				/>
				<ObjectStatItem
					icon={LinkIcon}
					label='Interaction Refs'
					value={jobStatistics.total_interaction_references_synced}
					subtitle='Interaction links to related records'
					iconColor='#B45309'
					showBorder={true}
					backgroundColor='#fff8e6'
				/>
				<ObjectStatItem
					icon={EmailOutlinedIcon}
					label='Interaction Msgs'
					value={jobStatistics.total_interaction_messages_synced}
					subtitle='Individual email bodies'
					iconColor='#C2255C'
					backgroundColor='#fff0f3'
				/>
			</Box>
		</Box>
	);
};

AdminEmailObjectSyncedStatistics.propTypes = {
	jobStatistics: PropTypes.object.isRequired,
};

export default AdminEmailObjectSyncedStatistics;
